import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import BankNameField from "../components/BankNameField";
import { fractionFor, useBankDirectory } from "../lib/bankDirectory";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";

const API_URL = process.env.REACT_APP_API_URL;

const VALID_US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC","PR",
];

const emptyForm = () => ({
  name: "",
  dba: "",
  ein: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
  business_url: "",
  bank_name: "",
  routing_number: "",
  account_number: "",
  bank_fraction: "",
  logo_url: "",
  bank_nickname: "",
});

const inputClass =
  "w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 text-sm";
const labelClass = "block mb-1 text-sm text-gray-600 font-medium";

export default function CompanySettings() {
  const auth = useAuth();
  const { companies: allCompanies, switchCompany, refetch, isDemo, subscriptionData } = useCompany();
  // Only the person's own companies: shared ones are managed by their owners.
  const companies = allCompanies.filter((c) => !c.shared);
  const isAdmin = subscriptionData?.is_admin === true;
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null); // company_id being edited, or "new"
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [connectingId, setConnectingId] = useState(null);
  // Details can change outside PayClear (account.olenesoft.com, other Olene apps), so load fresh.
  useEffect(() => { refetch(); }, [refetch]);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [dashboardLoadingId, setDashboardLoadingId] = useState(null);
  // The edited company's Olene bank accounts (backend/companies/banks.py): masked, and the one used
  // for checks. Most companies have one, and then this section looks as it always has.
  const [oleneBanks, setOleneBanks] = useState({ banks: [], selected: null });
  const [adding, setAdding] = useState(null); // the "Add another account" form, or null

  const token = auth.user?.access_token;
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const handleSignOut = () => {
    auth.signOut();
  };

  const openNew = () => {
    setForm(emptyForm());
    setOleneBanks({ banks: [], selected: null });
    setAdding(null);
    setEditingId("new");
  };

  const loadBanks = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/companies/${companyId}/banks`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setOleneBanks({ banks: data.banks || [], selected: data.selected });
      const sel = (data.banks || []).find((b) => b.id === data.selected);
      setForm((p) => ({ ...p, bank_nickname: sel?.nickname || "" }));
    } catch {}
  };

  const bankLabel = (b) =>
    `${b.bankName}${b.nickname ? ` (${b.nickname})` : ""} ••••${b.accountLast4}`;

  // Applies a company returned by a bank change to the open form.
  const showBank = (company) =>
    setForm((p) => ({
      ...p,
      bank_name: company.bank_name,
      routing_number: company.routing_number,
      account_number: company.account_number,
    }));

  const handleChooseBank = async (bankId) => {
    try {
      const res = await fetch(`${API_URL}/companies/${editingId}/bank-selection`, {
        method: "PUT", headers, body: JSON.stringify({ bank_id: bankId }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      showBank(await res.json());
      await loadBanks(editingId);
      await refetch();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("save_failed"), text: err.message });
    }
  };

  const handleAddBank = async () => {
    if (!adding.bank_name.trim() || !/^\d{9}$/.test(adding.routing_number) || !/^\d{4,17}$/.test(adding.account_number)) {
      Swal.fire({ icon: "error", title: t("invalid_routing_title"), text: t("add_bank_invalid") });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/companies/${editingId}/banks`, {
        method: "POST", headers, body: JSON.stringify(adding),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({})))?.detail;
        throw new Error(detail?.message || `${res.status}`);
      }
      showBank(await res.json());
      setAdding(null);
      await loadBanks(editingId);
      await refetch();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("save_failed"), text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (company) => {
    setForm({
      name:           company.name,
      dba:            company.dba || "",
      ein:            company.ein || "",
      address:        company.address,
      city:           company.city,
      state:          company.state,
      zip:            company.zip,
      phone:          company.phone,
      email:          company.email || "",
      business_url:   company.business_url || "",
      bank_name:      company.bank_name,
      routing_number: company.routing_number,
      account_number: company.account_number,
      bank_fraction:  company.bank_fraction || "",
      logo_url:       company.logo_url || "",
      bank_nickname:  "",
    });
    setOleneBanks({ banks: [], selected: null });
    setAdding(null);
    setEditingId(company.company_id);
    if (company.olene_linked) loadBanks(company.company_id);
  };

  const validate = () => {
    if (!form.name.trim()) { Swal.fire({ icon: "error", title: t("required"), text: t("company_name_required") }); return false; }
    if (form.dba.trim().length > 120) {
      Swal.fire({ icon: "error", title: t("invalid_dba_title"), text: t("invalid_dba_msg") }); return false;
    }
    if (form.ein.trim() && !/^\d{2}-?\d{7}$/.test(form.ein.trim())) {
      Swal.fire({ icon: "error", title: t("invalid_ein_title"), text: t("invalid_ein_msg") }); return false;
    }
    if (form.routing_number && !/^\d{9}$/.test(form.routing_number)) {
      Swal.fire({ icon: "error", title: t("invalid_routing_title"), text: t("invalid_routing_msg") }); return false;
    }
    if (form.account_number && !/^\d{6,17}$/.test(form.account_number)) {
      Swal.fire({ icon: "error", title: t("invalid_account_title"), text: t("invalid_account_msg") }); return false;
    }
    if (form.state && !VALID_US_STATES.includes(form.state.toUpperCase())) {
      Swal.fire({ icon: "error", title: t("invalid_state_title"), text: t("invalid_state_msg") }); return false;
    }
    if (form.zip && !/^\d{5}(-\d{4})?$/.test(form.zip)) {
      Swal.fire({ icon: "error", title: t("invalid_zip_title"), text: t("invalid_zip_msg") }); return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const body = { ...form, state: form.state.toUpperCase() };
    // A nickname is only offered (and sent) when the company's Olene company has several accounts.
    const inUse = oleneBanks.banks.find((b) => b.id === oleneBanks.selected);
    if (!inUse || oleneBanks.banks.length < 2 || (form.bank_nickname || "") === (inUse.nickname || "")) {
      delete body.bank_nickname;
    }
    try {
      let res;
      if (editingId === "new") {
        res = await fetch(`${API_URL}/companies`, {
          method: "POST", headers, body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_URL}/companies/${editingId}`, {
          method: "PUT", headers, body: JSON.stringify(body),
        });
      }
      if (!res.ok) throw new Error(`${res.status}`);
      await refetch();
      setEditingId(null);
    } catch (err) {
      Swal.fire({ icon: "error", title: t("save_failed"), text: `Could not save company: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/companies/${companyId}/set-default`, {
        method: "PUT", headers,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      await refetch();
      switchCompany(companyId);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    }
  };

  const handleConnectStripe = async (company) => {
    setConnectingId(company.company_id);
    try {
      const res = await fetch(`${API_URL}/stripe/connect/onboard`, {
        method: "POST",
        headers,
        body: JSON.stringify({ company_id: company.company_id, origin: window.location.origin }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: `Could not start Stripe connection: ${err.message}` });
      setConnectingId(null);
    }
  };

  const handleStripeDashboard = async (company) => {
    setDashboardLoadingId(company.company_id);
    try {
      const res = await fetch(`${API_URL}/stripe/connect/dashboard-link/${company.company_id}`, { headers });
      if (!res.ok) throw new Error(`${res.status}`);
      const { url } = await res.json();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "Could not open Stripe dashboard. Please try again." });
    } finally {
      setDashboardLoadingId(null);
    }
  };

  const handleDisconnectStripe = async (company) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Disconnect Stripe?",
      text: "Clients will no longer be able to pay invoices online for this company.",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Disconnect",
    });
    if (!result.isConfirmed) return;
    setDisconnectingId(company.company_id);
    try {
      const res = await fetch(`${API_URL}/stripe/connect/${company.company_id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      await refetch();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleDelete = async (company) => {
    const result = await Swal.fire({
      icon: "warning",
      title: t("delete_company_confirm", { name: company.name }),
      text: t("delete_company_body"),
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: t("delete"),
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/companies/${company.company_id}`, {
        method: "DELETE", headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || `${res.status}`);
      }
      await refetch();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("delete_failed"), text: err.message });
    }
  };

  // Known banks suggested as the name is typed (lib/bankDirectory.js); picking one fills the numbers.
  const directory = useBankDirectory(token);
  const pickBank = (s) => setForm((p) => ({ ...p, bank_name: s.bankName, routing_number: s.routing, bank_fraction: s.fraction }));
  const typeRouting = (set) => (e) => {
    const routing = e.target.value.replace(/\D/g, "").slice(0, 9);
    set((p) => {
      const known = routing.length === 9 && !(p.bank_fraction || p.fraction) ? fractionFor(directory, routing) : undefined;
      return { ...p, routing_number: routing, ...(known ? ("fraction" in p ? { fraction: known } : { bank_fraction: known }) : {}) };
    });
  };

  const f = (field, placeholder) => (
    <input
      className={inputClass}
      value={form[field]}
      placeholder={placeholder}
      onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
    />
  );

  const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/svg+xml"];
  const MAX_LOGO_BYTES = 512 * 1024; // 512 KB

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      Swal.fire({ icon: "error", title: "Invalid file type", text: "Logo must be a PNG, JPG, GIF, WebP, or SVG image." });
      e.target.value = "";
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      Swal.fire({ icon: "error", title: "File too large", text: "Logo must be under 512 KB. Try compressing the image first." });
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setForm((p) => ({ ...p, logo_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex-1 p-3 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">{t("company_settings_title")}</h1>
              <p className="text-sm text-gray-500 mt-1">{t("company_settings_subtitle")}</p>
            </div>
            <button
              onClick={!isDemo ? openNew : undefined}
              disabled={isDemo}
              title={isDemo ? t("demo_lock_companies") : undefined}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${isDemo ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-green-500 text-white hover:bg-green-600"}`}
            >
              {t("add_company_btn")}
            </button>
          </div>

          {/* Company list */}
          <div className="space-y-4">
            {companies.map((company) => (
              <div
                key={company.company_id}
                className={`bg-white rounded-lg shadow p-5 border-l-4 ${
                  company.is_default ? "border-green-500" : "border-gray-200"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-800">{company.name}</h2>
                      {company.dba && <span className="text-sm text-gray-500">{t("dba_prefix")} {company.dba}</span>}
                      {company.is_default && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          {t("default_label")}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-500 space-y-0.5">
                      {company.address && <p>{company.address}</p>}
                      {(company.city || company.state) && (
                        <p>{[company.city, company.state, company.zip].filter(Boolean).join(", ")}</p>
                      )}
                      {company.phone && <p>{company.phone}</p>}
                      {company.email && <p>{company.email}</p>}
                      {company.bank_name && (
                        <p className="mt-1">
                          <span className="font-medium text-gray-600">{company.bank_name}</span>
                          {company.routing_number && ` · Routing: ${company.routing_number}`}
                          {company.bank_fraction && ` · ${company.bank_fraction}`}
                          {company.account_number && ` · Acct: ****${company.account_number.slice(-4)}`}
                        </p>
                      )}
                      {isAdmin && company.stripe_connect_account_id && (
                        <p className="mt-1 flex items-center gap-1 text-purple-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                          <span className="text-xs font-medium">Stripe payments enabled</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 ml-4 flex-wrap">
                    {!company.is_default && (
                      <button
                        onClick={() => handleSetDefault(company.company_id)}
                        className="text-xs border border-green-400 text-green-600 px-2 py-1 rounded hover:bg-green-50"
                      >
                        {t("set_default")}
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(company)}
                      className="text-xs border border-gray-300 text-gray-600 px-2 py-1 rounded hover:bg-gray-50"
                    >
                      {t("edit")}
                    </button>
                    {isAdmin && (company.stripe_connect_account_id ? (
                      <>
                        <button
                          onClick={() => handleStripeDashboard(company)}
                          disabled={dashboardLoadingId === company.company_id}
                          title="Manage payouts, refunds, and payments"
                          className="text-xs border border-purple-300 text-purple-600 px-2 py-1 rounded hover:bg-purple-50 disabled:opacity-50 flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          {dashboardLoadingId === company.company_id ? "…" : "Stripe Dashboard"}
                        </button>
                        <button
                          onClick={() => handleDisconnectStripe(company)}
                          disabled={disconnectingId === company.company_id}
                          className="text-xs border border-red-300 text-red-500 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                          {disconnectingId === company.company_id ? "…" : "Stripe Connected"}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleConnectStripe(company)}
                        disabled={connectingId === company.company_id || isDemo}
                        title={isDemo ? "Not available in demo" : "Accept invoice payments online via Stripe"}
                        className="text-xs border border-purple-300 text-purple-600 px-2 py-1 rounded hover:bg-purple-50 disabled:opacity-50 flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        {connectingId === company.company_id ? "Redirecting…" : "Connect Stripe"}
                      </button>
                    ))}
                    {companies.length > 1 && (
                      <button
                        onClick={() => handleDelete(company)}
                        className="text-xs border border-red-300 text-red-500 px-2 py-1 rounded hover:bg-red-50"
                      >
                        {t("delete")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {companies.length === 0 && (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
                {t("no_companies")}
              </div>
            )}
          </div>

          {/* Edit / Add form */}
          {editingId !== null && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-5">
                    {editingId === "new" ? t("add_company_title") : t("edit_company_title")}
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <label className={labelClass}>{t("company_name_field")}</label>
                      {f("name")}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelClass}>{t("company_dba")}</label>
                        {f("dba")}
                      </div>
                      <div>
                        <label className={labelClass}>{t("company_ein")}</label>
                        {f("ein", "12-3456789")}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>{t("street_address")}</label>
                      {f("address")}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className={labelClass}>{t("city")}</label>
                        {f("city")}
                      </div>
                      <div>
                        <label className={labelClass}>{t("state")}</label>
                        {f("state")}
                      </div>
                      <div>
                        <label className={labelClass}>{t("zip")}</label>
                        {f("zip")}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>{t("phone")}</label>
                      {f("phone")}
                    </div>
                    <div>
                      <label className={labelClass}>{t("company_email")}</label>
                      {f("email")}
                    </div>
                    <div>
                      <label className={labelClass}>Business Website <span className="text-gray-400 font-normal">(optional — used for Stripe payments)</span></label>
                      {f("business_url", "https://yoursite.com or facebook.com/yourbusiness")}
                    </div>
                    <hr className="my-2" />
                    <div>
                      <label className={labelClass}>Logo</label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        onChange={handleLogoChange}
                        className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                      />
                      {form.logo_url && form.logo_url.startsWith("data:image/") && (
                        <div className="mt-2 flex items-center gap-3">
                          <img src={form.logo_url} alt="Logo preview" className="h-12 object-contain border border-gray-200 rounded p-1" />
                          <button
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, logo_url: "" }))}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      {form.logo_url && !form.logo_url.startsWith("data:image/") && form.logo_url !== "" && (
                        <p className="text-xs text-gray-500 mt-1">Logo saved (non-image file).</p>
                      )}
                    </div>
                    <hr className="my-2" />
                    {/* Shared with the company's Olene account (backend/olene_account.py). */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t("manual_bank_title")}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{t("manual_bank_hint")}</p>
                    </div>
                    {oleneBanks.banks.length >= 2 && (
                      <div>
                        <label className={labelClass}>{t("bank_for_checks")}</label>
                        <select
                          className={inputClass}
                          value={oleneBanks.selected || ""}
                          onChange={(e) => e.target.value && handleChooseBank(e.target.value)}
                        >
                          {!oleneBanks.selected && <option value="">{t("choose_bank_option")}</option>}
                          {oleneBanks.banks.map((b) => (
                            <option key={b.id} value={b.id}>{bankLabel(b)}</option>
                          ))}
                        </select>
                        {!oleneBanks.selected && (
                          <p className="text-xs text-red-600 mt-1">{t("choose_bank_for_checks")}</p>
                        )}
                      </div>
                    )}
                    <div className={oleneBanks.banks.length >= 2 && oleneBanks.selected ? "grid grid-cols-2 gap-2" : ""}>
                      <div>
                        <label className={labelClass}>{t("bank_name_field")}</label>
                        <BankNameField className={inputClass} value={form.bank_name} directory={directory} state={form.state}
                          onChange={(v) => setForm((p) => ({ ...p, bank_name: v }))} onPick={pickBank} />
                      </div>
                      {oleneBanks.banks.length >= 2 && oleneBanks.selected && (
                        <div>
                          <label className={labelClass}>{t("bank_nickname")}</label>
                          {f("bank_nickname")}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelClass}>{t("routing_number")}</label>
                        <input className={inputClass} value={form.routing_number || ""} inputMode="numeric"
                          onChange={typeRouting(setForm)} />
                      </div>
                      <div>
                        <label className={labelClass}>{t("account_number")}</label>
                        {f("account_number")}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <label className={labelClass}>{t("bank_fraction")}</label>
                        {f("bank_fraction", "87-863/640")}
                      </div>
                      <p className="text-xs text-gray-500 pb-1">{t("bank_fraction_hint")}</p>
                    </div>
                    {oleneBanks.selected && (
                      adding ? (
                        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-500">{t("add_another_account")}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={labelClass}>{t("bank_name_field")}</label>
                              <BankNameField className={inputClass} value={adding.bank_name} directory={directory} state={form.state}
                                onChange={(v) => setAdding((a) => ({ ...a, bank_name: v }))}
                                onPick={(s) => setAdding((a) => ({ ...a, bank_name: s.bankName, routing_number: s.routing, fraction: s.fraction }))} />
                            </div>
                            <div>
                              <label className={labelClass}>{t("bank_nickname")}</label>
                              <input className={inputClass} value={adding.nickname}
                                onChange={(e) => setAdding((a) => ({ ...a, nickname: e.target.value }))} />
                            </div>
                            <div>
                              <label className={labelClass}>{t("routing_number")}</label>
                              <input className={inputClass} value={adding.routing_number} inputMode="numeric" onChange={typeRouting(setAdding)} />
                            </div>
                            <div>
                              <label className={labelClass}>{t("account_number")}</label>
                              <input className={inputClass} value={adding.account_number} inputMode="numeric"
                                onChange={(e) => setAdding((a) => ({ ...a, account_number: e.target.value.replace(/\D/g, "") }))} />
                            </div>
                            <div>
                              <label className={labelClass}>{t("bank_fraction")}</label>
                              <input className={inputClass} value={adding.fraction || ""} placeholder="87-863/640"
                                onChange={(e) => setAdding((a) => ({ ...a, fraction: e.target.value }))} />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setAdding(null)} className="px-3 py-1.5 rounded bg-gray-200 text-gray-700 text-xs">
                              {t("cancel")}
                            </button>
                            <button onClick={handleAddBank} disabled={saving}
                              className="px-3 py-1.5 rounded bg-green-500 text-white text-xs font-medium disabled:opacity-50">
                              {t("add_account")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAdding({ bank_name: "", nickname: "", routing_number: "", account_number: "", fraction: "" })}
                          className="text-xs text-blue-600 hover:underline self-start"
                        >
                          {t("add_another_account")}
                        </button>
                      )
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm"
                    >
                      {t("cancel")}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-5 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 text-sm font-medium disabled:opacity-50"
                    >
                      {saving ? t("saving") : t("save")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6">
            <a href="/" className="text-blue-500 hover:underline text-sm">
              {t("back_to_dashboard")}
            </a>
          </div>
        </div>
      </div>
      </Sidebar>
    </div>
  );
}
